/* Explicit commerce events only. Purchasing must not depend on this module. */
(function () {
  const core = halldaleAnalyticsContext();
  let client;
  let context;
  let permission;
  let pageViewSession;
  const allowed = () => {
    try {
      return permission?.analyticsProcessingAllowed() === true;
    } catch {
      return false;
    }
  };
  const readCookie = () =>
    document.cookie
      .split('; ')
      .find((part) => part.startsWith(`${core.cookie}=`))
      ?.slice(core.cookie.length + 1);
  const write = (value, maxAge = 1800) => {
    document.cookie = `${core.cookie}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax; Secure`;
  };
  const persistCart = async () => {
    if (!context || !allowed()) return;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [core.attribute]: core.encode(context) } }),
        signal: AbortSignal.timeout(1500),
      });
      return response.ok;
    } catch {
      return false;
    }
  };
  const capture = (name, properties = {}) => {
    if (!client || !context || !allowed()) return false;
    context.touched_at = Date.now();
    write(core.encode(context));
    try {
      client.capture(name, { ...properties, ...core.properties(context) });
      return true;
    } catch {
      return false;
    }
  };
  window.HalldaleAnalytics = {
    capture,
    isAllowed: allowed,
    // Normalize useful seed/design terms; omit addresses, URLs, long numbers, and oversized input.
    searchProperties(value) {
      const raw = String(value ?? '').trim();
      const safe = raw.length <= 64 && /^[\p{L}\p{M}0-9 '’-]+$/u.test(raw) && raw.replace(/\D/g, '').length < 5;
      return {
        search_term: safe ? raw.toLowerCase().normalize('NFKC').replace(/\s+/g, ' ') : undefined,
        search_term_status: !raw ? 'empty' : safe ? 'captured' : 'redacted',
      };
    },
    persistCart,
    filterEvent(event) {
      if (!context || !allowed()) return null;
      const url = new URL(window.location.href);
      // Only explicit, reviewed properties plus safe SDK page metadata survive.
      const keys = [
        'token',
        '$process_person_profile',
        '$is_identified',
        'product_id',
        'variant_id',
        'color',
        'size',
        'value',
        'currency',
        'quantity',
        'destination_product',
        'destination_product_id',
        'selection',
        'request_source',
        'product_surface',
        'search_term',
        'search_term_status',
        'result_count',
        'suggestion_count',
        'selection_method',
        'tab',
        'previous_tab',
        'interaction_method',
        'gallery_index',
        'media_id',
        'previous_color',
        'previous_size',
        '$insert_id',
        '$browser',
        '$browser_version',
        '$os',
        '$device_type',
        '$lib',
        '$lib_version',
        '$window_id',
        '$recording_status',
        '$session_recording_start_reason',
      ];
      if (event.event === '$snapshot') keys.push('$snapshot_data', '$snapshot_bytes', '$snapshot_host');
      const selected = Object.fromEntries(
        keys.filter((key) => event.properties?.[key] !== undefined).map((key) => [key, event.properties[key]]),
      );
      if (selected.search_term !== undefined) {
        Object.assign(selected, window.HalldaleAnalytics.searchProperties(selected.search_term));
      }
      event.properties = {
        ...selected,
        ...core.properties(context),
        distinct_id: context.distinct_id,
        $current_url: `${url.origin}${url.pathname}`,
        $host: url.host,
        $pathname: url.pathname,
        $referrer: '',
        $referring_domain: '',
        $geoip_disable: true,
      };
      delete event.$set;
      delete event.$set_once;
      delete event.properties.$initial_current_url;
      delete event.properties.$initial_referrer;
      return event;
    },
    prepare(privacy) {
      permission = privacy;
      if (!allowed()) {
        const hadContext = Boolean(readCookie());
        context = undefined;
        write('', 0);
        try {
          sessionStorage.removeItem('halldale_newsletter_pending');
        } catch {
          /* Storage may be unavailable. */
        }
        if (hadContext) {
          // Clear the old attribution bridge; never transmit it after withdrawal.
          fetch(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { [core.attribute]: '' } }),
          }).catch(() => {});
        }
        return null;
      }
      context = core.create(
        readCookie(),
        window.location.href,
        window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop',
      );
      context.is_test = context.is_test || window.Shopify?.designMode === true;
      write(core.encode(context));
      return context;
    },
    ready(sdk) {
      client = sdk;
      if (!allowed() || !context) return;
      client.register(core.properties(context));
      // Own page-view order: the SDK's automatic capture can race consent/readiness.
      if (pageViewSession !== context.session_id && capture('$pageview')) pageViewSession = context.session_id;
      document.dispatchEvent(new CustomEvent('halldale:analytics-ready'));
    },
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-halldale-component="newsletter"] form').forEach((form) => {
      form.addEventListener('submit', () => {
        if (!allowed()) return;
        try {
          sessionStorage.setItem('halldale_newsletter_pending', crypto.randomUUID());
        } catch {
          /* No telemetry dependency. */
        }
      });
    });
    const newsletterSuccess = () => {
      if (!document.querySelector('[data-newsletter-success]') || !allowed()) return;
      try {
        const eventId = sessionStorage.getItem('halldale_newsletter_pending');
        if (eventId && capture('newsletter_subscribed', { $insert_id: eventId }))
          sessionStorage.removeItem('halldale_newsletter_pending');
      } catch {
        /* Do not collect form values. */
      }
    };
    newsletterSuccess();
    document.addEventListener('halldale:analytics-ready', newsletterSuccess);
    // Delegated: the cart drawer replaces its form whenever a line changes.
    const continuing = new WeakSet();
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest?.('[data-cart-form]');
      if (!form || continuing.has(form) || event.submitter?.name !== 'checkout' || !context || !allowed()) return;
      event.preventDefault();
      await persistCart();
      continuing.add(form);
      form.requestSubmit(event.submitter);
    });
  });
})();
